import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import AppShell from "../components/AppShell";

const GOALS = [
  { id: "adelgazar", label: "Adelgazar" },
  { id: "mantener", label: "Mantener peso" },
  { id: "ganar_masa", label: "Ganar masa muscular" },
];

export default function EditProfile() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/profile").then((r) => {
      const p = r.data;
      setForm({
        name: p.name || "",
        age: String(p.age ?? ""),
        sex: p.sex || "mujer",
        height_cm: String(p.height_cm ?? ""),
        weight_kg: String(p.weight_kg ?? ""),
        goal: p.goal || "adelgazar",
      });
    }).catch(() => {
      toast.error("No pudimos cargar tu perfil.");
      navigate("/panel");
    });
  }, [navigate]);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const age = parseInt(form.age, 10);
    const height = parseFloat(form.height_cm);
    const weight = parseFloat(form.weight_kg);

    if (!form.name.trim()) return toast.error("Ingresa tu nombre.");
    if (!Number.isFinite(age) || age < 10 || age > 120) return toast.error("Edad debe estar entre 10 y 120 años.");
    if (!Number.isFinite(height) || height < 100 || height > 250) return toast.error("Estatura debe estar entre 100 y 250 cm.");
    if (!Number.isFinite(weight) || weight < 30 || weight > 300) return toast.error("Peso debe estar entre 30 y 300 kg.");

    setLoading(true);
    try {
      // Backend POST /profile is idempotent: if a profile already exists, it updates it in place.
      // Personal payment/admin fields cannot be touched from this endpoint (server-side enforced).
      await api.post("/profile", { name: form.name.trim(), age, sex: form.sex, height_cm: height, weight_kg: weight, goal: form.goal });
      await refresh();
      toast.success("Perfil actualizado correctamente.");
      navigate("/panel");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo actualizar el perfil.");
    } finally {
      setLoading(false);
    }
  };

  if (!form) return (
    <AppShell title="Editar perfil">
      <div className="text-[var(--mep-text-muted)]">Cargando...</div>
    </AppShell>
  );

  return (
    <AppShell
      title="Editar perfil"
      subtitle="Actualiza tus datos y recalcularemos automáticamente tu meta, rango de referencia y recomendaciones."
    >
      <motion.form
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        onSubmit={handleSubmit}
        className="max-w-2xl space-y-6"
        data-testid="edit-profile-form"
      >
        <Field label="Nombre">
          <input
            data-testid="edit-name-input"
            type="text" required value={form.name}
            onChange={(e) => update("name", e.target.value)} className="input-mep"
          />
        </Field>

        <div className="grid md:grid-cols-2 gap-6">
          <Field label="Edad (años)">
            <input
              data-testid="edit-age-input"
              type="number" min={10} max={120} required
              value={form.age} onChange={(e) => update("age", e.target.value)} className="input-mep"
            />
          </Field>
          <Field label="Sexo">
            <div className="grid grid-cols-2 gap-3">
              {["mujer", "hombre"].map((s) => (
                <button
                  key={s} type="button"
                  data-testid={`edit-sex-${s}`}
                  onClick={() => update("sex", s)}
                  className={`py-3 rounded-lg border capitalize transition ${
                    form.sex === s
                      ? "bg-[var(--mep-pink)] border-[var(--mep-pink)] text-white"
                      : "bg-[var(--mep-surface)] border-[var(--mep-border)] hover:border-[var(--mep-gold)]/50"
                  }`}
                >{s}</button>
              ))}
            </div>
          </Field>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <Field label="Estatura (cm)">
            <input
              data-testid="edit-height-input"
              type="number" step="0.1" min={100} max={250} required
              value={form.height_cm} onChange={(e) => update("height_cm", e.target.value)} className="input-mep"
            />
          </Field>
          <Field label="Peso actual (kg)">
            <input
              data-testid="edit-weight-input"
              type="number" step="0.1" min={30} max={300} required
              value={form.weight_kg} onChange={(e) => update("weight_kg", e.target.value)} className="input-mep"
            />
            <p className="text-xs text-[var(--mep-text-muted)] mt-2">
              Editar tu peso actual actualiza el perfil, pero no borra tu historial de progreso anterior.
            </p>
          </Field>
        </div>

        <Field label="Tu objetivo">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {GOALS.map((g) => (
              <button
                key={g.id} type="button"
                data-testid={`edit-goal-${g.id}`}
                onClick={() => update("goal", g.id)}
                className={`py-3 px-4 rounded-lg border text-sm transition ${
                  form.goal === g.id
                    ? "bg-[var(--mep-pink)] border-[var(--mep-pink)] text-white"
                    : "bg-[var(--mep-surface)] border-[var(--mep-border)] hover:border-[var(--mep-gold)]/50"
                }`}
              >{g.label}</button>
            ))}
          </div>
        </Field>

        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <button
            data-testid="edit-submit-btn"
            type="submit"
            disabled={loading}
            className="btn-pink px-8 py-4 font-semibold w-full sm:w-auto"
          >
            {loading ? "Guardando..." : "Guardar cambios"}
          </button>
          <button
            data-testid="edit-cancel-btn"
            type="button"
            onClick={() => navigate("/panel")}
            className="px-8 py-4 font-medium w-full sm:w-auto rounded-full border border-[var(--mep-border)] text-[var(--mep-text-muted)] hover:text-white hover:border-[var(--mep-gold)]/40 transition"
          >
            Cancelar
          </button>
        </div>
      </motion.form>

      <style>{`.input-mep { width:100%; background: var(--mep-surface); border:1px solid var(--mep-border); border-radius:8px; padding: 0.75rem 1rem; color:white; outline:none; transition: border-color .2s; }
.input-mep:focus { border-color: var(--mep-pink); }`}</style>
    </AppShell>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)] mb-2 block">{label}</label>
    {children}
  </div>
);
